/**
 * Decoded cue buffers and the pipeline's measurements (Phase 8). Files are fetched from the
 * processed assets and decoded through an `OfflineAudioContext`, which the autoplay policy
 * leaves alone, so a match can load its sounds before the first click; the sidecar
 * `audio/bank.json` supplies each file's play-time gain and loop points.
 */
import {
  AUDIO_BANK,
  AudioSidecarSchema,
  getAudioCue,
  processedAudioPath,
  type AudioCue,
  type AudioSidecar,
} from '@steelwake/shared/data';

import { assetUrl } from '../game/assets/paths.js';

export type FileMeta = AudioSidecar['files'][string];

const SIDECAR_PATH = 'audio/bank.json';

/** The decode context: the live one once it exists, else an offline one at 48 kHz. */
export type DecoderSource = () => BaseAudioContext | null;

type OfflineContextCtor = new (
  channels: number,
  length: number,
  sampleRate: number,
) => OfflineAudioContext;

/** WebKit builds without the unprefixed name still carry the prefixed one. */
function offlineContextCtor(): OfflineContextCtor | null {
  const g = globalThis as {
    OfflineAudioContext?: OfflineContextCtor;
    webkitOfflineAudioContext?: OfflineContextCtor;
  };
  return g.OfflineAudioContext ?? g.webkitOfflineAudioContext ?? null;
}

export class AudioLibrary {
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly pending = new Map<string, Promise<void>>();
  private sidecar: AudioSidecar['files'] | null = null;
  private sidecarPending: Promise<void> | null = null;
  private offline: OfflineAudioContext | null = null;
  /** Fetched bytes waiting for a context to decode them (no offline context on this browser). */
  private readonly deferred = new Map<string, ArrayBuffer>();
  /** Files that failed to fetch or decode (reported once, then skipped). */
  readonly failed = new Set<string>();

  constructor(private readonly decoder: DecoderSource | null = null) {}

  /** Loads the sidecar and every file of the given cues; resolves when all are decoded. */
  async prefetch(cueIds: readonly string[], signal?: AbortSignal): Promise<void> {
    const cues = cueIds.map(getAudioCue);
    const files = new Set<string>();
    for (const cue of cues) for (const f of cue.files) files.add(processedAudioPath(f));
    await Promise.all([
      this.loadSidecar(signal),
      ...[...files].map((path) => this.load(path, signal)),
    ]);
  }

  /** The decoded buffer for a processed path, when it has arrived. */
  get(path: string): AudioBuffer | undefined {
    return this.buffers.get(path);
  }

  meta(path: string): FileMeta | undefined {
    return this.sidecar?.[path];
  }

  /** The processed paths of a cue's files that are ready to play. */
  ready(cue: AudioCue): string[] {
    return cue.files.map(processedAudioPath).filter((p) => this.buffers.has(p));
  }

  get loaded(): number {
    return this.buffers.size;
  }

  private loadSidecar(signal?: AbortSignal): Promise<void> {
    if (this.sidecar) return Promise.resolve();
    this.sidecarPending ??= (async () => {
      const res = await fetch(assetUrl(SIDECAR_PATH), signal ? { signal } : undefined);
      if (!res.ok) throw new Error(`audio sidecar: HTTP ${String(res.status)}`);
      this.sidecar = AudioSidecarSchema.parse((await res.json()) as unknown).files;
    })().finally(() => {
      this.sidecarPending = null;
    });
    return this.sidecarPending;
  }

  private load(path: string, signal?: AbortSignal): Promise<void> {
    if (this.buffers.has(path) || this.failed.has(path)) return Promise.resolve();
    const pending = this.pending.get(path);
    if (pending) return pending;
    const task = (async () => {
      try {
        const res = await fetch(assetUrl(path), signal ? { signal } : undefined);
        if (!res.ok) throw new Error(`HTTP ${String(res.status)}`);
        const bytes = await res.arrayBuffer();
        const ctx = this.context();
        if (!ctx) {
          // No offline context here (some WebKit builds): decode once the live one exists.
          this.deferred.set(path, bytes);
          return;
        }
        const buffer = await ctx.decodeAudioData(bytes);
        this.buffers.set(path, buffer);
      } catch (err) {
        if (signal?.aborted) return;
        this.failed.add(path);
        console.warn(`[audio] ${path}: ${err instanceof Error ? err.message : String(err)}`);
      }
    })().finally(() => {
      this.pending.delete(path);
    });
    this.pending.set(path, task);
    return task;
  }

  /** Decodes what was fetched before any context existed (called once the engine unlocks). */
  async decodeDeferred(): Promise<void> {
    const ctx = this.context();
    if (!ctx || this.deferred.size === 0) return;
    const entries = [...this.deferred.entries()];
    this.deferred.clear();
    await Promise.all(
      entries.map(async ([path, bytes]) => {
        try {
          this.buffers.set(path, await ctx.decodeAudioData(bytes));
        } catch (err) {
          this.failed.add(path);
          console.warn(`[audio] ${path}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }),
    );
  }

  /** How many files wait for a context. */
  get deferredCount(): number {
    return this.deferred.size;
  }

  private context(): BaseAudioContext | null {
    const live = this.decoder?.();
    if (live) return live;
    if (!this.offline) {
      const Ctor = offlineContextCtor();
      if (!Ctor) return null;
      this.offline = new Ctor(1, 1, 48_000);
    }
    return this.offline;
  }
}

/** Every cue id in the bank. */
export function allCueIds(): string[] {
  return AUDIO_BANK.cues.map((c) => c.id);
}
